// Mirrored in applications/platform-app/components/web-analytics — change both.
import React, { useEffect, useState } from 'react';
import { useDash } from '../context';
import { useWebQuery } from '../useWebQuery';
import { BreakdownCard, useBreakdown, useFunnel, useTimeseries, useTrack } from './shared';
import { LineChart } from '../charts/LineChart';
import { BarList } from '../charts/BarList';
import { Card, Empty, ErrorLine, MoreButton, Muted, Note, Tag, Td, Th, threatTone, rowButton } from '../ui';
import { SiteFav } from '../SiteSwitcher';
import type { Kpis, SecurityResponse, SitesTableResponse, SitesTableRow } from '../types';
import { METRIC_NAME, RUBY, bucketLabel, fmt, formatMetric, kpiValue, pct, type MetricId } from '../format';

export const NEW_SITE_MSG = 'Tracking is live — data appears within a minute of the first visit';

/** KPI tiles — clicking one selects the chart metric. */
export const KpiTiles: React.FC<{ kpis?: Kpis; previous?: Kpis; company: boolean }> = ({ kpis, previous, company }) => {
  const d = useDash();
  const today = d.state.range.range === 'today';
  const tiles: Array<{ id: MetricId; label: string; inverse?: boolean }> = [
    { id: 'visitors', label: 'Visitors' },
    { id: 'pageviews', label: 'Page views' },
    { id: 'avgVisitSec', label: 'Avg. visit' },
    { id: 'bounceRate', label: 'Bounce rate', inverse: true },
    { id: 'conversionRate', label: 'Conversion' },
    company ? { id: 'signups', label: 'Sign-ups' } : { id: 'revenueCents', label: 'Online revenue' },
  ];
  return (
    <div className="grid gap-3.5 grid-cols-1 sm:grid-cols-3 xl:grid-cols-6">
      {tiles.map((t) => {
        const v = kpis ? kpiValue(kpis, t.id) : 0;
        const p = previous ? kpiValue(previous, t.id) : undefined;
        const sel = d.state.metric === t.id;
        let delta: React.ReactNode = null;
        if (!d.filtered && d.state.range.compare && p !== undefined) {
          const vs = <Muted>{today ? 'vs all of yesterday' : 'vs previous'}</Muted>;
          if (p === 0) delta = v === 0 ? <><Muted>No change</Muted> {vs}</> : <><span className="text-green-600">New</span> {vs}</>;
          else {
            const ch = ((v - p) / p) * 100;
            const good = t.inverse ? ch < 0 : ch > 0;
            delta = <><span className={good ? 'text-green-600' : ''} style={good ? undefined : { color: RUBY }}>{ch > 0 ? '▲' : ch < 0 ? '▼' : ''} {Math.abs(ch).toFixed(1)}%</span> {vs}</>;
          }
        }
        return (
          <button
            key={t.id} type="button" aria-pressed={sel} data-testid={`wa-kpi-${t.id}`}
            onClick={() => d.setMetric(t.id)}
            className="bg-white border rounded-2xl p-4 text-left min-w-0 transition hover:border-[#fecdd3]"
            style={sel ? { borderColor: RUBY, boxShadow: '0 0 0 3px #FFF1F3' } : { borderColor: '#e7e5e4' }}
          >
            <div className="text-[11px] text-stone-500 font-medium">{t.label}</div>
            <div className="text-[22px] font-bold mt-1 tracking-tight text-stone-900">{kpis ? formatMetric(t.id, v) : '—'}</div>
            <div className="text-[11px] font-semibold mt-1 min-h-[16px]">{delta}</div>
          </button>
        );
      })}
    </div>
  );
};

/** Metric-over-time card shared by Overview and Acquisition. */
export const MetricChart: React.FC<{ metric: MetricId; title?: string; height?: number }> = ({ metric, title, height = 220 }) => {
  const d = useDash();
  const ts = useTimeseries(metric);
  const showPrev = d.state.range.compare && !d.filtered && !!ts.data?.previous;
  const pts = ts.data?.points || [];
  return (
    <Card title={title || `${METRIC_NAME[metric]} over time`} testId="wa-metric-chart">
      <div className="flex gap-3.5 text-[11px] text-stone-500 mb-1.5">
        <span><i className="inline-block w-2.5 h-[3px] rounded-sm mr-1.5 align-middle" style={{ background: RUBY }} />This period</span>
        {showPrev && <span><i className="inline-block w-2.5 h-[3px] rounded-sm mr-1.5 align-middle bg-stone-400" />{d.state.range.range === 'today' ? 'All of yesterday' : 'Previous period'}</span>}
      </div>
      {ts.error ? <ErrorLine message={ts.error} /> : (
        <LineChart
          values={pts.map((p) => p.value)}
          previous={showPrev ? ts.data!.previous!.map((p) => p.value) : undefined}
          labels={pts.map((p) => bucketLabel(p.label))}
          height={height}
          format={(v) => formatMetric(metric, v)}
        />
      )}
    </Card>
  );
};

/** `_all` only: one row per website; a row opens that site. */
const SitesTable: React.FC = () => {
  const d = useDash();
  const base = useTrack(useWebQuery<SitesTableResponse>({
    api: d.api, siteId: '_all', range: d.state.range, filters: [], want: null,
    unfiltered: () => d.api.sitesTable(d.state.range),
  }));
  // Filtered: the lake's per-site breakdown (no unique visitors there — visits stand in).
  const filtered = useBreakdown('site', 50, d.filtered);
  let rows: SitesTableRow[] = base.data?.rows || [];
  if (d.filtered && filtered.data) {
    const threat = new Map(rows.map((r) => [r.siteId, r.threat]));
    rows = filtered.data.rows.map((r) => {
      const s = d.sites.find((x) => x.siteId === r.value);
      const conv = r.orders || r.signups;
      return { siteId: r.value, name: s?.name || r.value, kind: s?.kind || 'store', visitors: null, visits: r.visits, conversions: conv, conversionRate: r.visits ? conv / r.visits : 0, threat: threat.get(r.value) || 'Low' };
    });
  }
  const host = (id: string) => d.sites.find((s) => s.siteId === id)?.hostnames[0];
  return (
    <Card title={<>Websites <Muted>· click a site to open its analytics</Muted></>} className="mt-3.5" testId="wa-sites-table">
      {base.error ? <ErrorLine message={base.error} /> : !rows.length ? <Empty /> : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead><tr><Th>Website</Th><Th>Type</Th><Th num>Visitors</Th><Th num>Visits</Th><Th num>Conversions</Th><Th num>Conv. rate</Th><Th>Threat level</Th><Th /></tr></thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.siteId} {...rowButton(() => d.setSite(r.siteId), `Open ${r.name}`)} data-testid={`wa-site-row-${r.siteId}`}>
                  <Td>
                    <span className="inline-flex gap-2 items-center"><SiteFav site={{ siteId: r.siteId, name: r.name, kind: r.kind === 'company' ? 'company' : 'store' }} size={22} /><b>{r.name}</b></span>
                    {host(r.siteId) && <div className="text-stone-500 text-[11px] ml-[30px]">{host(r.siteId)}</div>}
                  </Td>
                  <Td>{r.kind === 'company' ? <Tag tone="red">RubyTill</Tag> : <Tag tone="gray">Storefront</Tag>}</Td>
                  <Td num>{r.visitors === null ? <span title="Unique visitors are only counted for the busiest sites">—</span> : fmt(r.visitors)}</Td>
                  <Td num>{fmt(r.visits)}</Td>
                  <Td num>{fmt(r.conversions)}</Td>
                  <Td num>{pct(r.conversionRate, 2)}</Td>
                  <Td><Tag tone={threatTone(r.threat)}>{r.threat}</Tag></Td>
                  <Td num style={{ color: RUBY, fontWeight: 600 }}>Open →</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!d.filtered && base.data?.truncated && <Note>Unique visitors are counted for the {base.data.limit} busiest sites; the rest show —.</Note>}
    </Card>
  );
};

export const MiniFunnel: React.FC = () => {
  const d = useDash();
  const f = useFunnel();
  const steps = f.data?.steps || [];
  const first = steps[0]?.count || 0;
  if (f.error) return <ErrorLine message={f.error} />;
  if (!first) return <Empty />;
  return (
    <BarList
      limit={10}
      rows={steps.map((s) => ({ key: s.name, label: s.label, value: s.count, onClick: () => d.setTab('conversions') }))}
    />
  );
};

const Stat: React.FC<{ label: string; value: React.ReactNode; ruby?: boolean }> = ({ label, value, ruby }) => (
  <div><div className="text-[11px] text-stone-500">{label}</div><div className="text-lg font-bold" style={ruby ? { color: RUBY } : undefined}>{value}</div></div>
);

export const ThreatMeter: React.FC<{ score: number }> = ({ score }) => (
  <div className="flex-1 h-2.5 rounded-full relative" style={{ background: 'linear-gradient(90deg,#22c55e,#eab308,#DC143C)' }}>
    <i className="absolute -top-1 w-1 h-[18px] bg-[#111] rounded-sm" style={{ left: `${Math.min(98, Math.max(0, score))}%` }} />
  </div>
);

const SecuritySnapshot: React.FC = () => {
  const d = useDash();
  const sec = useTrack(useWebQuery<SecurityResponse>({
    api: d.api, siteId: d.siteId, range: d.state.range, filters: [], want: null,
    unfiltered: () => d.api.security(d.siteId, d.state.range),
  }));
  const s = sec.data;
  return (
    <Card title="Security snapshot" action={<MoreButton onClick={() => d.setTab('security')}>Open Security →</MoreButton>}>
      {sec.error ? <ErrorLine message={sec.error} /> : (
        <>
          <div className="flex items-center gap-3.5"><b>{s?.level || '—'}</b><ThreatMeter score={s?.score ?? 0} /></div>
          <div className="grid grid-cols-2 gap-2 mt-3.5">
            <Stat label="Blocked requests" value={fmt(s?.blocked ?? 0)} />
            <Stat label="Bot traffic" value={`${s?.botPct ?? 0}%`} />
            <Stat label="Failed logins" value={fmt(s?.failedLogins ?? 0)} />
            <Stat label="Open alerts" value={s?.openIncidents ?? 0} ruby />
          </div>
        </>
      )}
    </Card>
  );
};

export const OverviewTab: React.FC = () => {
  const d = useDash();
  const sum = d.summary;
  const company = d.siteKind === 'company';
  const k = sum.data?.kpis;
  const empty = !!k && k.visits === 0 && k.pageviews === 0;
  // A brand-new site (nothing in the last 12 months) gets the "tracking is live" message.
  const [brandNew, setBrandNew] = useState(false);
  useEffect(() => {
    setBrandNew(false);
    if (!empty || d.filtered || d.siteId === '_all') return;
    let off = false;
    d.api.summary(d.siteId, { range: '12m', compare: false })
      .then((r) => { if (!off) setBrandNew(r.kpis.visits === 0 && r.kpis.pageviews === 0); })
      .catch(() => undefined);
    return () => { off = true; };
  }, [empty, d.filtered, d.siteId, d.api]);

  return (
    <div>
      {empty && (
        <div className="bg-white border border-stone-200 rounded-2xl px-4 py-3 mb-3.5 text-[13px] text-stone-600" data-testid="wa-empty">
          {brandNew ? NEW_SITE_MSG : 'No data yet for this period'}
        </div>
      )}
      {sum.error && <ErrorLine message={sum.error} />}
      <KpiTiles kpis={k} previous={sum.data?.previous} company={company} />
      <div className="mt-3.5"><MetricChart metric={d.state.metric} /></div>
      {d.siteId === '_all' && d.mode === 'admin' && <SitesTable />}
      <div className="grid gap-3.5 mt-3.5 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        <BreakdownCard title="Top pages" dim="page" viewAll value={(r) => r.pv} testId="wa-card-pages" />
        <BreakdownCard title="Traffic sources" dim="source" viewAll testId="wa-card-sources" />
        <BreakdownCard title="Devices" dim="device" donut />
      </div>
      <div className="grid gap-3.5 mt-3.5 grid-cols-1 md:grid-cols-2 xl:grid-cols-3">
        <BreakdownCard title="Countries" dim="country" viewAll />
        <Card title={company ? 'Sign-up funnel' : 'Shopping funnel'} action={<MoreButton onClick={() => d.setTab('conversions')}>View all →</MoreButton>}>
          <MiniFunnel />
        </Card>
        <SecuritySnapshot />
      </div>
    </div>
  );
};

export default OverviewTab;
